import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, User, Hash, FileText, Globe, Loader2, Video as VideoIcon, Mic2, ListVideo } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useNostrQuery, useNostrProfile, getCachedProfile } from '../hooks/useNostrQuery';
import { useDisplayName } from '../hooks/useNip05Verified';
import PostCard from '../components/PostCard';
import FollowButton from '../components/FollowButton';
import UserBadges from '../components/UserBadges';
import VideoCard from '../components/VideoCard';
import { useVideoDirectory, useAllPlaylists } from '../hooks/useVideo';
import { usePodcastDirectory } from '../hooks/usePodcast';
import { videoCategoryLabel, playlistPath } from '../lib/video';
import { podcastPath, podcastCategoryLabel } from '../lib/podcast';

// ══════════════════════════════════════════════════════════════════
//  RELIABLE NIP-50 SEARCH RELAYS (verified working as of 2026)
// ══════════════════════════════════════════════════════════════════

const NIP50_PROFILE_RELAYS = [
  'wss://relay.nostr.band',
  'wss://search.nos.today',
  'wss://relay.ditto.pub',
  'wss://relay.noswhere.com',
];

const NIP50_POST_RELAYS = [
  'wss://relay.nostr.band',
  'wss://search.nos.today',
  'wss://relay.ditto.pub',
  'wss://relay.noswhere.com',
];

// ══════════════════════════════════════════════════════════════════
//  WEBSOCKET SEARCH — each relay gets its own clean connection
//  with proper lifecycle management (open → REQ → collect → close)
// ══════════════════════════════════════════════════════════════════

interface RelaySearchOptions {
  relayUrl: string;
  filters: any[];
  signal: AbortSignal;
  timeout?: number;
}

/** Open a fresh WebSocket to a relay, send a REQ, collect events, close cleanly. */
function searchRelay({ relayUrl, filters, signal, timeout = 8000 }: RelaySearchOptions): Promise<any[]> {
  return new Promise((resolve) => {
    const results: any[] = [];
    const subId = 's_' + Math.random().toString(36).slice(2, 8);
    let ws: WebSocket | null = null;
    let done = false;
    let timer: any = null;

    const finish = () => {
      if (done) return;
      done = true;
      if (timer) clearTimeout(timer);
      resolve(results);
      try { ws?.close(); } catch {}
    };

    // Abort from parent
    if (signal.aborted) { resolve([]); return; }
    signal.addEventListener('abort', finish, { once: true });

    try {
      ws = new WebSocket(relayUrl);

      ws.onopen = () => {
        if (signal.aborted) { finish(); return; }
        try {
          ws!.send(JSON.stringify(['REQ', subId, ...filters]));
        } catch { finish(); }
      };

      ws.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data);
          if (!Array.isArray(data)) return;

          if (data[0] === 'EVENT' && data[1] === subId && data[2]) {
            const ev = data[2];
            if (ev && ev.id && ev.pubkey) {
              if (!Array.isArray(ev.tags)) ev.tags = [];
              results.push(ev);
            }
          } else if (data[0] === 'EOSE' && data[1] === subId) {
            finish();
          } else if (data[0] === 'CLOSED' && data[1] === subId) {
            finish();
          } else if (data[0] === 'NOTICE') {
            // Some relays send NOTICE for unsupported filters — treat as done
            finish();
          }
        } catch {}
      };

      ws.onerror = () => finish();
      ws.onclose = () => { if (!done) finish(); };

      // Hard timeout — don't wait forever
      timer = setTimeout(finish, timeout);
    } catch {
      resolve([]);
    }
  });
}

// ══════════════════════════════════════════════════════════════════
//  PRIMAL CACHE SEARCH — uses Primal's custom cache API
// ══════════════════════════════════════════════════════════════════

function searchPrimal(query: string, signal: AbortSignal): Promise<any[]> {
  return new Promise((resolve) => {
    const results: any[] = [];
    const subId = 'ps_' + Math.random().toString(36).slice(2, 8);
    let ws: WebSocket | null = null;
    let done = false;
    let timer: any = null;

    const finish = () => {
      if (done) return;
      done = true;
      if (timer) clearTimeout(timer);
      resolve(results);
      try { ws?.close(); } catch {}
    };

    if (signal.aborted) { resolve([]); return; }
    signal.addEventListener('abort', finish, { once: true });

    try {
      ws = new WebSocket('wss://cache2.primal.net/v1');

      ws.onopen = () => {
        if (signal.aborted) { finish(); return; }
        ws!.send(JSON.stringify(['REQ', subId, { cache: ['user_search', { query, limit: 20 }] }]));
      };

      ws.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data);
          if (Array.isArray(data) && data[0] === 'EVENT' && data[1] === subId) {
            const ev = data[2];
            if (ev && ev.kind === 0 && ev.pubkey) results.push(ev);
          } else if (Array.isArray(data) && data[0] === 'EOSE' && data[1] === subId) {
            finish();
          }
        } catch {}
      };

      ws.onerror = () => finish();
      ws.onclose = () => { if (!done) finish(); };
      timer = setTimeout(finish, 6000);
    } catch { resolve([]); }
  });
}

// ══════════════════════════════════════════════════════════════════
//  PROFILE SEARCH HOOK — queries Primal + NIP-50 relays in parallel
// ══════════════════════════════════════════════════════════════════

function useProfileSearch(query: string, enabled: boolean) {
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<any>(null);

  const doSearch = useCallback(async (q: string) => {
    if (abortRef.current) abortRef.current.abort();

    if (!q || q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // Fire all search sources in parallel — Primal cache + NIP-50 relays
      const searches = [
        searchPrimal(q, controller.signal).catch(() => []),
        ...NIP50_PROFILE_RELAYS.map(relay =>
          searchRelay({
            relayUrl: relay,
            filters: [{ kinds: [0], search: q, limit: 20 }],
            signal: controller.signal,
            timeout: 8000,
          }).catch(() => [])
        ),
      ];

      // Progressive results — resolve as they arrive
      let firstBatch = true;
      const allSettled = searches.map(p =>
        p.then(events => {
          if (controller.signal.aborted || events.length === 0) return;

          // Merge incrementally
          setResults(prev => {
            const byPubkey = new Map<string, any>();
            // Keep existing results
            prev.forEach(ev => {
              const existing = byPubkey.get(ev.pubkey);
              if (!existing || ev.created_at > existing.created_at) {
                byPubkey.set(ev.pubkey, ev);
              }
            });
            // Add new results
            events.forEach((ev: any) => {
              if (ev.kind === 0 && ev.pubkey) {
                const existing = byPubkey.get(ev.pubkey);
                if (!existing || ev.created_at > existing.created_at) {
                  byPubkey.set(ev.pubkey, ev);
                }
              }
            });
            return [...byPubkey.values()];
          });

          if (firstBatch) {
            firstBatch = false;
            setLoading(false);
          }
        })
      );

      await Promise.allSettled(allSettled);

      if (!controller.signal.aborted) {
        setLoading(false);
      }
    } catch {} finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setResults([]);
      setLoading(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, enabled, doSearch]);

  useEffect(() => {
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  return { results, loading };
}

// ══════════════════════════════════════════════════════════════════
//  NIP-50 POST/CONTENT SEARCH — searches notes via NIP-50
// ══════════════════════════════════════════════════════════════════

function usePostSearch(query: string, enabled: boolean) {
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<any>(null);

  const doSearch = useCallback(async (q: string) => {
    if (abortRef.current) abortRef.current.abort();

    if (!q || q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const searches = NIP50_POST_RELAYS.map(relay =>
        searchRelay({
          relayUrl: relay,
          filters: [{ kinds: [1], search: q, limit: 40 }],
          signal: controller.signal,
          timeout: 10000,
        }).catch(() => [])
      );

      let firstBatch = true;
      const allSettled = searches.map(p =>
        p.then(events => {
          if (controller.signal.aborted || events.length === 0) return;

          setResults(prev => {
            const byId = new Map<string, any>();
            prev.forEach(ev => byId.set(ev.id, ev));
            events.forEach((ev: any) => {
              if (ev.kind === 1 && ev.id && !byId.has(ev.id)) {
                byId.set(ev.id, ev);
              }
            });
            return [...byId.values()].sort((a, b) => b.created_at - a.created_at);
          });

          if (firstBatch) {
            firstBatch = false;
            setLoading(false);
          }
        })
      );

      await Promise.allSettled(allSettled);
      if (!controller.signal.aborted) setLoading(false);
    } catch {} finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setResults([]);
      setLoading(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, enabled, doSearch]);

  useEffect(() => {
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  return { results, loading };
}

// ══════════════════════════════════════════════════════════════════
//  NOSTR IDENTIFIER RESOLVER
// ══════════════════════════════════════════════════════════════════

function resolveNostrIdentifier(input: string): string | null {
  const trimmed = input.trim().replace(/^nostr:/, '');

  if (trimmed.startsWith('npub1')) {
    try {
      const { type, data } = nip19.decode(trimmed);
      if (type === 'npub') return `/profile/${data}`;
    } catch {}
  }

  if (trimmed.startsWith('nprofile1')) {
    try {
      const { type, data } = nip19.decode(trimmed);
      if (type === 'nprofile') return `/profile/${(data as any).pubkey}`;
    } catch {}
  }

  if (trimmed.startsWith('note1')) {
    try {
      const { type, data } = nip19.decode(trimmed);
      if (type === 'note') return `/note/${data}`;
    } catch {}
  }

  if (trimmed.startsWith('nevent1')) {
    try {
      const { type, data } = nip19.decode(trimmed);
      if (type === 'nevent') return `/note/${(data as any).id}`;
    } catch {}
  }

  if (trimmed.startsWith('naddr1')) {
    try {
      const { type, data } = nip19.decode(trimmed);
      if (type === 'naddr' && (data as any).kind === 30023) return `/ink/${trimmed}`;
    } catch {}
  }

  if (/^[0-9a-f]{64}$/i.test(trimmed)) {
    return `/profile/${trimmed}`;
  }

  return null;
}

// ══════════════════════════════════════════════════════════════════
//  MAIN SEARCH PAGE COMPONENT
// ══════════════════════════════════════════════════════════════════

export default function SearchPage() {
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'users' | 'posts' | 'hashtags' | 'videos'>('users');
  const navigate = useNavigate();

  // Detect Nostr identifiers and navigate directly
  const nostrPath = useMemo(() => resolveNostrIdentifier(query), [query]);

  // Look up the profile for npub/nprofile/hex so we can show a result card
  const npubPubkey = useMemo(() => {
    const trimmed = query.trim().replace(/^nostr:/, '');
    try {
      if (trimmed.startsWith('npub1')) {
        const { type, data } = nip19.decode(trimmed);
        if (type === 'npub') return data as string;
      }
      if (trimmed.startsWith('nprofile1')) {
        const { type, data } = nip19.decode(trimmed);
        if (type === 'nprofile') return (data as any).pubkey as string;
      }
      if (/^[0-9a-f]{64}$/i.test(trimmed)) return trimmed;
    } catch {}
    return null;
  }, [query]);

  // Fetch the profile for the npub/hex pubkey
  const { events: npubProfileEvents } = useNostrQuery({
    filters: npubPubkey ? [{ kinds: [0], authors: [npubPubkey], limit: 1 }] : [],
    enabled: !!npubPubkey,
  });

  // Fetch BCH community profiles — active BCH Nostr members plus anyone
  // posting with BCH hashtags (posts no longer require a BCH hashtag).
  const [profileSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 30);
  const { events: bchPosts } = useNostrQuery({
    filters: [
      { kinds: [1], '#t': ['bchnostr'], limit: 100, since: profileSince },
      { kinds: [1], '#t': ['bch'], limit: 100, since: profileSince },
      { kinds: [1], '#t': ['bitcoincash'], limit: 100, since: profileSince },
    ],
    enabled: activeTab === 'users',
  });

  // Collect unique pubkeys from BCH posts
  const bchPubkeys = useMemo(() => [...new Set(bchPosts.map(e => e.pubkey))], [bchPosts]);

  // Fetch their profiles
  const { events: profileEvents } = useNostrQuery({
    filters: bchPubkeys.length > 0 ? [{ kinds: [0], authors: bchPubkeys.slice(0, 80), limit: 80 }] : [],
    enabled: activeTab === 'users' && bchPubkeys.length > 0,
  });

  // Filter BCH community profiles by search query
  const filteredProfiles = useMemo(() => {
    if (!query.trim() || query.length < 2) return profileEvents.slice(0, 20);
    const q = query.toLowerCase();
    return profileEvents.filter(ev => {
      try {
        const p = JSON.parse(ev.content);
        const name = (p.display_name || p.name || '').toLowerCase();
        const nip05 = (p.nip05 || '').toLowerCase();
        const about = (p.about || '').toLowerCase();
        return name.includes(q) || nip05.includes(q) || about.includes(q);
      } catch { return false; }
    }).slice(0, 20);
  }, [profileEvents, query]);

  // Global Nostr profile search via NIP-50 + Primal
  const isTextSearch = query.trim().length >= 2 && !nostrPath;
  const { results: globalResults, loading: globalLoading } = useProfileSearch(query.trim(), activeTab === 'users' && isTextSearch);

  // Merge: BCH community matches first, then global results (deduped)
  const allUserResults = useMemo(() => {
    const seen = new Set<string>();
    const merged: any[] = [];
    filteredProfiles.forEach(ev => {
      if (!seen.has(ev.pubkey)) { seen.add(ev.pubkey); merged.push(ev); }
    });
    globalResults.forEach(ev => {
      if (!seen.has(ev.pubkey)) { seen.add(ev.pubkey); merged.push(ev); }
    });
    return merged;
  }, [filteredProfiles, globalResults]);

  // Hashtag search — search posts by hashtag
  const hashtagQuery = query.startsWith('#') ? query.slice(1).toLowerCase() : query.toLowerCase();
  const [postSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 7);
  const { events: hashtagPosts, loading: hashtagLoading } = useNostrQuery({
    filters: hashtagQuery.length >= 2 ? [{ kinds: [1], '#t': [hashtagQuery], limit: 50, since: postSince }] : [],
    enabled: activeTab === 'hashtags' && hashtagQuery.length >= 2,
  });

  // NIP-50 full-text post search
  const postQuery = query.trim();
  const { results: nip50Posts, loading: nip50PostsLoading } = usePostSearch(
    postQuery,
    activeTab === 'posts' && postQuery.length >= 2 && !nostrPath,
  );

  // Combined posts for the posts tab: NIP-50 results + hashtag fallback
  const combinedPosts = useMemo(() => {
    if (activeTab !== 'posts') return [];
    const byId = new Map<string, any>();
    nip50Posts.forEach(ev => byId.set(ev.id, ev));
    // Also include hashtag matches as bonus results
    if (hashtagQuery.length >= 2) {
      hashtagPosts.forEach(ev => {
        if (!byId.has(ev.id)) byId.set(ev.id, ev);
      });
    }
    return [...byId.values()].sort((a, b) => b.created_at - a.created_at);
  }, [activeTab, nip50Posts, hashtagPosts, hashtagQuery]);

  // Also run hashtag query in background when on posts tab for supplemental results
  const { events: postsTabHashtagPosts } = useNostrQuery({
    filters: postQuery.length >= 2 ? [{ kinds: [1], '#t': [postQuery.toLowerCase()], limit: 30, since: postSince }] : [],
    enabled: activeTab === 'posts' && postQuery.length >= 2,
  });

  // Final posts list merges NIP-50 + hashtag results
  const finalPosts = useMemo(() => {
    if (activeTab !== 'posts') return [];
    const byId = new Map<string, any>();
    nip50Posts.forEach(ev => byId.set(ev.id, ev));
    postsTabHashtagPosts.forEach(ev => { if (!byId.has(ev.id)) byId.set(ev.id, ev); });
    return [...byId.values()].sort((a, b) => b.created_at - a.created_at);
  }, [activeTab, nip50Posts, postsTabHashtagPosts]);

  const postsLoading = nip50PostsLoading;

  // ── Video search ───────────────────────────────────────────────
  // Videos, video channels and playlists all live in Nostr events we
  // already query for discovery, so search is a local filter over that
  // set — no extra relay round-trip, and it works offline from cache.
  const { videos: allVideos, loading: videosLoading } = useVideoDirectory();
  const { playlists: allPlaylists } = useAllPlaylists();
  // Podcast shows are matched here too, so one search covers both media
  // surfaces of a creator's professional profile.
  const { podcasts: allPodcasts } = usePodcastDirectory();

  const videoQuery = query.trim().toLowerCase().replace(/^#/, '');

  const matchedVideos = useMemo(() => {
    if (activeTab !== 'videos') return [];
    if (videoQuery.length < 2) return allVideos.slice(0, 24);
    return allVideos.filter(v =>
      v.title.toLowerCase().includes(videoQuery) ||
      v.description.toLowerCase().includes(videoQuery) ||
      v.hashtags.some(t => t.includes(videoQuery)) ||
      videoCategoryLabel(v.category).toLowerCase().includes(videoQuery),
    ).slice(0, 40);
  }, [activeTab, allVideos, videoQuery]);

  const matchedPlaylists = useMemo(() => {
    if (activeTab !== 'videos' || videoQuery.length < 2) return [];
    return allPlaylists.filter(p =>
      p.title.toLowerCase().includes(videoQuery) ||
      p.description.toLowerCase().includes(videoQuery),
    ).slice(0, 10);
  }, [activeTab, allPlaylists, videoQuery]);

  const matchedPodcasts = useMemo(() => {
    if (activeTab !== 'videos' || videoQuery.length < 2) return [];
    return allPodcasts.filter(p =>
      p.name.toLowerCase().includes(videoQuery) ||
      p.description.toLowerCase().includes(videoQuery) ||
      p.hosts.some(h => h.toLowerCase().includes(videoQuery)),
    ).slice(0, 10);
  }, [activeTab, allPodcasts, videoQuery]);

  /** Creators who publish video and whose name matches the query. */
  const matchedChannels = useMemo(() => {
    if (activeTab !== 'videos' || videoQuery.length < 2) return [];
    const creators = new Map<string, number>();
    for (const v of allVideos) creators.set(v.pubkey, (creators.get(v.pubkey) || 0) + 1);
    // Names come from the profile cache the video cards already populate,
    // so this is a filter over data we hold, not a new query.
    return [...creators.entries()]
      .filter(([pk]) => {
        const cached = getCachedProfile(pk);
        const name = ((cached?.display_name || cached?.name || '') as string).toLowerCase();
        const nip05 = ((cached?.nip05 || '') as string).toLowerCase();
        return name.includes(videoQuery) || nip05.includes(videoQuery);
      })
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([pubkey, count]) => ({ pubkey, count }));
  }, [activeTab, allVideos, videoQuery]);

  return (
    <div>
      <div className="sticky top-0 lg:top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border p-4">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && nostrPath) navigate(nostrPath); }}
            placeholder="Search people, npub, #hashtags, posts, videos..."
            className="w-full bg-muted rounded-full pl-10 pr-4 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            autoFocus
          />
        </div>
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {[
            { id: 'users' as const, icon: User, label: 'People' },
            { id: 'posts' as const, icon: FileText, label: 'Posts' },
            { id: 'videos' as const, icon: VideoIcon, label: 'Videos' },
            { id: 'hashtags' as const, icon: Hash, label: 'Hashtags' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${activeTab === tab.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
            >
              <tab.icon className="w-3 h-3" /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Nostr identifier match banner */}
      {nostrPath && (
        <div className="border-b border-border">
          {npubPubkey ? (
            <div>
              <div className="px-4 py-2 bg-primary/5 border-b border-border">
                <p className="text-xs text-primary font-medium">Nostr user found — press Enter or tap to view profile</p>
              </div>
              {npubProfileEvents.length > 0 ? (
                <UserResult event={npubProfileEvents[0]} />
              ) : (
                <Link to={nostrPath} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-6 h-6 text-primary/60" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate">{npubPubkey.slice(0, 12)}...{npubPubkey.slice(-8)}</p>
                    <p className="text-xs text-muted-foreground">Loading profile...</p>
                  </div>
                </Link>
              )}
            </div>
          ) : (
            <Link to={nostrPath} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Search className="w-5 h-5 text-primary/60" />
              </div>
              <div>
                <p className="text-sm font-medium text-primary">Go to {nostrPath.startsWith('/ink/') ? 'article' : 'note'}</p>
                <p className="text-xs text-muted-foreground truncate max-w-[250px]">{query.trim().slice(0, 40)}...</p>
              </div>
            </Link>
          )}
        </div>
      )}

      {/* People */}
      {activeTab === 'users' && (
        <div className="divide-y divide-border">
          {!query && (
            <div className="px-4 py-3 bg-muted/30">
              <p className="text-xs text-muted-foreground">Showing BCH community members. Search by name or paste an npub to find anyone on Nostr.</p>
            </div>
          )}
          {allUserResults.length > 0 && (
            <>
              {filteredProfiles.length > 0 && globalResults.length > 0 && query.trim().length >= 2 && (
                <div className="px-4 py-2 bg-muted/30">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">BCH Community</p>
                </div>
              )}
              {filteredProfiles.map(ev => <UserResult key={ev.pubkey} event={ev} />)}
              {globalResults.length > 0 && query.trim().length >= 2 && (
                <div className="px-4 py-2 bg-muted/30">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold flex items-center gap-1">
                    <Globe className="w-3 h-3" /> All Nostr
                  </p>
                </div>
              )}
              {globalResults.filter(ev => !filteredProfiles.some(bp => bp.pubkey === ev.pubkey)).map(ev => <UserResult key={ev.pubkey} event={ev} />)}
            </>
          )}
          {allUserResults.length === 0 && !npubPubkey && !globalLoading && (
            <div className="text-center py-12 px-4">
              <User className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">{query ? `No users found for "${query}"` : 'Loading community...'}</p>
              {query && (
                <p className="text-xs text-muted-foreground/70 mt-2 max-w-xs mx-auto">
                  Try a different spelling, or paste their npub/nprofile to find them directly.
                </p>
              )}
            </div>
          )}
          {globalLoading && allUserResults.length === 0 && (
            <div className="flex items-center justify-center py-12 gap-2">
              <Loader2 className="w-5 h-5 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">Searching all of Nostr...</p>
            </div>
          )}
          {globalLoading && allUserResults.length > 0 && (
            <div className="flex items-center justify-center py-3 gap-2">
              <Loader2 className="w-4 h-4 text-primary animate-spin" />
              <p className="text-xs text-muted-foreground">Searching more relays...</p>
            </div>
          )}
        </div>
      )}

      {/* Posts — NIP-50 full-text search */}
      {activeTab === 'posts' && (
        <div>
          {postQuery.length < 2 ? (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">Type a keyword to search posts across Nostr</p>
              <div className="flex flex-wrap gap-2 justify-center mt-4">
                {['bitcoin cash', 'BCH', 'cashtokens', 'P2P cash', 'nostr'].map(term => (
                  <button key={term} onClick={() => setQuery(term)} className="px-3 py-1.5 rounded-full bg-primary/5 text-primary text-xs hover:bg-primary/10">
                    {term}
                  </button>
                ))}
              </div>
            </div>
          ) : postsLoading && finalPosts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">Searching posts...</p>
            </div>
          ) : finalPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-muted-foreground">No posts found for "{postQuery}"</p>
              <p className="text-xs text-muted-foreground/60 mt-1">Try different keywords or check spelling</p>
            </div>
          ) : (
            <>
              {finalPosts.map(ev => <PostCard key={ev.id} event={ev} />)}
              {postsLoading && (
                <div className="flex items-center justify-center py-4 gap-2">
                  <Loader2 className="w-4 h-4 text-primary animate-spin" />
                  <p className="text-xs text-muted-foreground">Searching more relays...</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Videos, channels and playlists.
          Results are explicitly labelled by TYPE so a video is never
          mistaken for a post, a channel, or a podcast. */}
      {activeTab === 'videos' && (
        <div>
          {videosLoading && allVideos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">Loading videos...</p>
            </div>
          ) : (
            <>
              {/* Channels */}
              {matchedChannels.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-muted/30 border-b border-border">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold flex items-center gap-1">
                      <VideoIcon className="w-3 h-3" /> Video channels
                    </p>
                  </div>
                  {matchedChannels.map(({ pubkey, count }) => (
                    <ChannelResult key={pubkey} pubkey={pubkey} videoCount={count} />
                  ))}
                </>
              )}

              {/* Playlists */}
              {matchedPlaylists.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-muted/30 border-b border-border">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold flex items-center gap-1">
                      <ListVideo className="w-3 h-3" /> Playlists
                    </p>
                  </div>
                  {matchedPlaylists.map(pl => (
                    <Link
                      key={`${pl.pubkey}:${pl.id}`}
                      to={playlistPath(pl.pubkey, pl.id)}
                      className="flex items-center gap-3 px-4 py-3 border-b border-border hover:bg-muted/40 transition-colors group"
                    >
                      <div className="w-16 aspect-video rounded-lg overflow-hidden bg-muted border border-border shrink-0">
                        {pl.image ? (
                          <img src={pl.image} alt="" loading="lazy" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ListVideo className="w-4 h-4 text-primary/40" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors">
                          {pl.title}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          Playlist · {pl.videoRefs.length} video{pl.videoRefs.length === 1 ? '' : 's'}
                        </p>
                      </div>
                    </Link>
                  ))}
                </>
              )}

              {/* Podcast shows — the other media surface of a professional profile. */}
              {matchedPodcasts.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-muted/30 border-b border-border">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold flex items-center gap-1">
                      <Mic2 className="w-3 h-3" /> Podcasts
                    </p>
                  </div>
                  {matchedPodcasts.map(show => (
                    <Link
                      key={show.pubkey}
                      to={podcastPath(show.pubkey)}
                      className="flex items-center gap-3 px-4 py-3 border-b border-border hover:bg-muted/40 transition-colors group"
                    >
                      {show.image ? (
                        <img src={show.image} alt="" loading="lazy" className="w-12 h-12 rounded-lg object-cover border border-border shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Mic2 className="w-5 h-5 text-primary/50" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors">
                          {show.name}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          Podcast · {podcastCategoryLabel(show.category)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </>
              )}

              {/* Videos */}
              {matchedVideos.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-muted/30 border-b border-border">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold flex items-center gap-1">
                      <VideoIcon className="w-3 h-3" />
                      {videoQuery.length < 2 ? 'Latest videos' : 'Videos'}
                    </p>
                  </div>
                  <div className="p-4 space-y-3">
                    {matchedVideos.map(v => (
                      <VideoCard key={`${v.pubkey}:${v.id}`} video={v} variant="row" />
                    ))}
                  </div>
                </>
              )}

              {matchedVideos.length === 0 && matchedChannels.length === 0 &&
               matchedPlaylists.length === 0 && matchedPodcasts.length === 0 && (
                <div className="text-center py-12 px-4">
                  <VideoIcon className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {videoQuery.length >= 2
                      ? `No videos found for "${query.trim()}"`
                      : 'No videos yet'}
                  </p>
                  <Link to="/videos" className="text-xs text-primary hover:underline mt-2 inline-block">
                    Browse BCH Video
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Hashtags */}
      {activeTab === 'hashtags' && (
        <div>
          {hashtagQuery.length < 2 ? (
            <div className="text-center py-12">
              <Hash className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">Type a hashtag to search tagged posts</p>
              <div className="flex flex-wrap gap-2 justify-center mt-4">
                {['bch', 'bitcoincash', 'cashtokens', 'p2p', 'defi', 'nostr'].map(tag => (
                  <button key={tag} onClick={() => setQuery('#' + tag)} className="px-3 py-1.5 rounded-full bg-primary/5 text-primary text-xs hover:bg-primary/10">
                    #{tag}
                  </button>
                ))}
              </div>
            </div>
          ) : hashtagLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">Searching #{hashtagQuery}...</p>
            </div>
          ) : hashtagPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-muted-foreground">No posts found for "#{hashtagQuery}"</p>
            </div>
          ) : (
            hashtagPosts.map(ev => <PostCard key={ev.id} event={ev} />)
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  USER RESULT CARD
// ══════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════
//  VIDEO CHANNEL RESULT CARD
//  A channel IS a Nostr profile, so this reuses the same identity —
//  there is no separate video account to look up.
// ══════════════════════════════════════════════════════════════════

function ChannelResult({ pubkey, videoCount }: { pubkey: string; videoCount: number }) {
  const { profile } = useNostrProfile(pubkey);
  const name = useDisplayName(pubkey, profile, pubkey.slice(0, 12));

  return (
    <Link
      to={`/videos/channel/${pubkey}`}
      className="flex items-center gap-3 px-4 py-3 border-b border-border hover:bg-muted/40 transition-colors group"
    >
      {profile?.picture ? (
        <img src={profile.picture} alt="" loading="lazy" className="w-12 h-12 rounded-full object-cover shrink-0" />
      ) : (
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <VideoIcon className="w-5 h-5 text-primary/60" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors">{name}</p>
          <UserBadges pubkey={pubkey} size="sm" showTooltip={false} />
        </div>
        <p className="text-[11px] text-muted-foreground">
          Video channel · {videoCount} video{videoCount === 1 ? '' : 's'}
        </p>
      </div>
    </Link>
  );
}

function UserResult({ event }: { event: any }) {
  try {
    const profile = JSON.parse(event.content);
    return (
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors">
        <Link to={`/profile/${event.pubkey}`} className="shrink-0">
          {profile.picture ? (
            <img src={profile.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-6 h-6 text-primary/60" />
            </div>
          )}
        </Link>
        <Link to={`/profile/${event.pubkey}`} className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="font-semibold text-sm truncate">{profile.display_name || profile.name || event.pubkey.slice(0, 12)}</p>
            <UserBadges pubkey={event.pubkey} size="sm" />
          </div>
          {profile.nip05 && <p className="text-xs text-muted-foreground truncate">{profile.nip05}</p>}
          {profile.about && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{profile.about}</p>}
        </Link>
        <FollowButton pubkey={event.pubkey} size="md" />
      </div>
    );
  } catch {
    return null;
  }
}

